import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import TradeNotificationPanel from "../../components/trade/TradeNotificationPanel";
import TradeCategoryManager from "../../components/trade/TradeCategoryManager";
import TradePasswordPanel from "../../components/trade/TradePasswordPanel";
import TradeProjectManager from "../../components/trade/TradeProjectManager";
import TradeSubscriberManager from "../../components/trade/TradeSubscriberManager";
import TradeUploadModal from "../../components/trade/TradeUploadModal";
import { bytesLabel, clearSession, formatDate, tradeGet, tradeJson } from "../../utils/tradeApi";
import "./trade.css";

export default function TradePortal() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const role = localStorage.getItem("userRole");
  const manager = role === "admin" || role === "office";
  const [projects, setProjects] = useState([]);
  const [capabilities, setCapabilities] = useState({
    canManageFiles: manager,
    canSendNotifications: manager,
    canManageTradeUsers: role === "admin",
    canManageCategories: role === "admin",
    canManageTradeProjects: role === "admin",
    canChangeTradePassword: role === "trade",
  });
  const [categories, setCategories] = useState([]);
  const [categoryId, setCategoryId] = useState("");
  const [files, setFiles] = useState([]);
  const [tab, setTab] = useState("drawings");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [categoryManagerOpen, setCategoryManagerOpen] = useState(false);

  const project = useMemo(() => projects.find((item) => item._id === projectId)
    || JSON.parse(localStorage.getItem("selectedTradeProject") || "null"), [projects, projectId]);
  const category = categories.find((item) => item._id === categoryId);

  useEffect(() => {
    setLoading(true); setError("");
    Promise.all([
      tradeGet("/api/trade/projects"),
      tradeGet(`/api/trade/projects/${projectId}/categories`),
      tradeGet("/api/trade/account/capabilities"),
    ]).then(([projectData, categoryData, capabilityData]) => {
      setProjects(projectData); setCategories(categoryData);
      setCapabilities(capabilityData);
      setCategoryId((current) => current || categoryData[0]?._id || "");
    }).catch((requestError) => setError(requestError.message))
      .finally(() => setLoading(false));
  }, [projectId]);

  const loadFiles = useCallback(async () => {
    if (!categoryId) { setFiles([]); return; }
    try { setFiles(await tradeGet(`/api/trade/projects/${projectId}/categories/${categoryId}/files`)); }
    catch (requestError) { setError(requestError.message); }
  }, [projectId, categoryId]);

  const refreshCategories = useCallback(async () => {
    const data = await tradeGet(`/api/trade/projects/${projectId}/categories`);
    setCategories(data);
    setCategoryId((current) => data.some((item) => item._id === current) ? current : (data[0]?._id || ""));
  }, [projectId]);

  const refreshProjects = useCallback(async (changedProjectId, enabled) => {
    const data = await tradeGet("/api/trade/projects");
    setProjects(data);
    if (changedProjectId === projectId && enabled === false) navigate("/trade/projects");
  }, [navigate, projectId]);

  useEffect(() => { loadFiles(); }, [loadFiles]);

  function logout() { clearSession(); navigate("/trade", { replace: true }); }

  async function download(file) {
    try {
      const result = await tradeGet(`/api/trade/files/${file._id}/download`);
      window.location.assign(result.downloadUrl);
    } catch (requestError) { setError(requestError.message); }
  }

  async function remove(file) {
    if (!window.confirm(`Delete “${file.display_name}” from the Trade portal? Existing seven-day links will be revoked.`)) return;
    try { await tradeJson(`/api/trade/files/${file._id}`, "DELETE"); await loadFiles(); }
    catch (requestError) { setError(requestError.message); }
  }

  const tabs = [["drawings", "Drawings"]];
  if (capabilities.canManageTradeProjects) tabs.push(["projects", "Projects"]);
  if (capabilities.canManageTradeUsers) tabs.push(["subscribers", "Subscribers"]);
  if (capabilities.canSendNotifications) tabs.push(["notifications", "Notifications"]);
  if (capabilities.canChangeTradePassword) tabs.push(["settings", "Account"]);

  return (
    <main className="trade-app">
      <aside className="trade-sidebar">
        <img className="trade-logo inverse" src="/images/gemterra-logo-20260112-t.png" alt="Gemterra" />
        <div className="trade-sidebar-project"><small>CURRENT PROJECT</small><strong>{project?.name || "Trade project"}</strong><button onClick={() => navigate("/trade/projects")}>Change project</button></div>
        <nav>{tabs.map(([value, label]) => <button key={value} className={tab === value ? "active" : ""} onClick={() => setTab(value)}><span>{label.slice(0, 1)}</span>{label}</button>)}</nav>
        <div className="trade-sidebar-user"><small>{role?.toUpperCase()}</small><strong>{localStorage.getItem("userName") || localStorage.getItem("userEmail")}</strong><button onClick={logout}>Sign out</button></div>
      </aside>

      <section className="trade-main">
        <header className="trade-main-header"><div><span className="trade-eyebrow trade-green">{tab.toUpperCase()}</span><h1>{tab === "drawings" ? "Current project drawings" : tabs.find(([value]) => value === tab)?.[1]}</h1></div>{role === "trade" && localStorage.getItem("usesDefaultPassword") === "true" && <button className="trade-default-notice" onClick={() => setTab("settings")}>Using default password · Change</button>}</header>
        {error && <div className="trade-alert error page-alert">{error}<button onClick={() => setError("")}>×</button></div>}
        {loading ? <div className="trade-loading">Loading Trade portal…</div> : (
          <>
            {tab === "drawings" && (
              <div className="trade-drawings-layout">
                <aside className="trade-category-list">
                  <div><span className="trade-eyebrow">DRAWING PACKAGES</span><strong>{categories.length} categories</strong>{capabilities.canManageCategories && <button className="trade-link-button trade-manage-packages" onClick={() => setCategoryManagerOpen(true)}>Manage packages</button>}</div>
                  {categories.map((item) => <button key={item._id} className={`trade-category-package ${categoryId === item._id ? "active" : ""}`} onClick={() => setCategoryId(item._id)}><span>{item.code}</span><strong>{item.name}</strong><small>View current files</small></button>)}
                  {!categories.length && <div className="trade-empty small">No categories assigned.</div>}
                </aside>
                <section className="trade-files-card">
                  <div className="trade-section-heading"><div><span className="trade-eyebrow trade-green">{category?.code || "—"}</span><h2>{category?.name || "Select a category"}</h2><p>{files.length} current file{files.length === 1 ? "" : "s"}, newest issue date first</p></div>{capabilities.canManageFiles && category && <div className="trade-action-row">{capabilities.canSendNotifications && <button className="trade-button secondary" disabled={!files.some((file) => file.status === "pending" || file.notification_status === "pending")} onClick={() => setTab("notifications")}>Notify</button>}<button className="trade-button primary" onClick={() => setUploadOpen(true)}>Upload</button></div>}</div>
                  {!files.length ? <div className="trade-empty"><h3>No drawings published</h3><p>There are no current files in this category.</p></div> : (
                    <div className="trade-table-wrap"><table className="trade-table"><thead><tr><th>Drawing</th><th>Format</th><th>Issue date</th><th>Drawing company</th><th>Uploaded</th><th>Status</th><th aria-label="Actions" /></tr></thead><tbody>{files.map((file) => <tr key={file._id}><td><strong>{file.display_name}</strong><small>{file.original_name}</small><small>{bytesLabel(file.file_size)}</small></td><td><span className={`trade-format ${String(file.format).toLowerCase()}`}>{file.format}</span></td><td>{formatDate(file.issue_date)}</td><td>{file.drawing_company}</td><td>{formatDate(file.published_at)}</td><td><span className={`trade-status ${file.status}`}>{file.status}</span></td><td><div className="trade-row-actions">{file.format === "PDF" && <button onClick={() => navigate(`/trade/pdf/${file._id}`)}>View</button>}<button onClick={() => download(file)}>Download</button>{capabilities.canManageFiles && <button className="danger" onClick={() => remove(file)}>Delete</button>}</div></td></tr>)}</tbody></table></div>
                  )}
                </section>
              </div>
            )}
            {tab === "projects" && capabilities.canManageTradeProjects && <TradeProjectManager onProjectsChanged={refreshProjects} />}
            {tab === "subscribers" && capabilities.canManageTradeUsers && <TradeSubscriberManager projects={projects} />}
            {tab === "notifications" && capabilities.canSendNotifications && <TradeNotificationPanel projectId={projectId} category={category} files={files} onSent={loadFiles} />}
            {tab === "settings" && <TradePasswordPanel />}
          </>
        )}
      </section>
      {uploadOpen && category && <TradeUploadModal projectId={projectId} category={category} onClose={() => setUploadOpen(false)} onUploaded={async () => { setUploadOpen(false); await loadFiles(); }} />}
      {categoryManagerOpen && capabilities.canManageCategories && <TradeCategoryManager projectId={projectId} onClose={() => setCategoryManagerOpen(false)} onChanged={refreshCategories} />}
    </main>
  );
}
